import type React from 'react';
import { useEffect } from 'react';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import type { EntitySchemaProps } from '@polyai/schemas';
import { EntityType } from '@polyai/stream/store/reducers/entities/types';
import IconButton from '@polyai/ui/components/atoms/IconButton';
import { TrashCanIcon } from '@polyai/ui/components/atoms/Icons';

import { FormTextInput } from 'components/molecules/Form/FormTextInput';

interface Props {
  readonly: boolean;
}

export const MultipleChoiceForm: React.FC<Props> = ({ readonly }) => {
  const { control, watch } = useFormContext<EntitySchemaProps>();

  const config = watch('config');

  const fieldMethods = useFieldArray({
    control,
    // @ts-ignore - complex nested path typing
    name: 'config.value.options',
  });

  const { fields, remove, append } = fieldMethods;

  // Add a new input field when option for last field changes
  const handleOptionChange = (index: number, value: string) => {
    const isLastInput = index === fields.length - 1;
    if (isLastInput && value !== '') {
      append('', { shouldFocus: false });
    }
  };

  useEffect(() => {
    if (config?.$case === EntityType.ENUM) {
      // Add empty field if no fields exist
      if (fields.length === 0) {
        append('', { shouldFocus: false });
      }
      // Add empty field if last element is not empty
      else if (fields.length > 0) {
        const lastFieldValue = config?.value?.options?.[fields.length - 1];
        if (lastFieldValue && lastFieldValue.trim() !== '') {
          append('', { shouldFocus: false });
        }
      }
    }
  }, [config, append, fields]);

  const handleRemoveOption = (index: number) => {
    remove(index);
  };

  if (config?.$case !== EntityType.ENUM) {
    return null;
  }

  return (
    <div className="flex flex-col gap-xs2">
      {fields.map((field, index) => {
        const isLastInput = index === fields.length - 1;
        const isEmptyLastInput =
          isLastInput &&
          (!config?.value?.options?.[index] ||
            config?.value?.options?.[index].trim() === '');

        // Don't render the last input in readonly mode
        if (isLastInput && readonly) {
          return null;
        }

        return (
          <div key={field.id} className="flex items-center gap-xs2">
            <div className="flex-1">
              <Controller
                control={control}
                name={`config.value.options.${index}`}
                render={({ field: controllerField, fieldState: { error } }) => (
                  <FormTextInput
                    {...controllerField}
                    controlKey={`config.value.options.${index}`}
                    errors={
                      error ? [error.message || 'This field is required'] : []
                    }
                    placeholder="Input option"
                    readOnly={readonly}
                    fullWidth
                    onChange={e => {
                      controllerField.onChange(e.target.value);
                      handleOptionChange(index, e.target.value);
                    }}
                  />
                )}
                rules={{
                  required: isLastInput ? false : 'This field is required',
                }}
              />
            </div>

            {!readonly && (
              <div className="flex-shrink-0">
                <IconButton
                  data-test-id={`delete-option-${index}`}
                  disabled={isEmptyLastInput || readonly}
                  onClick={() => handleRemoveOption(index)}
                >
                  <TrashCanIcon />
                </IconButton>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
